"use client";

import { Suspense, useState, useEffect } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import Link from "next/link";
import { signIn, useSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { EmailLoginForm } from "@/components/auth/email-login-form";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Mail, FileText, Upload, Eye, Clock } from "lucide-react";

function ClientLoginContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { data: session, status } = useSession();
  const t = useTranslations();
  const [error, setError] = useState<string | null>(null);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);

  useEffect(() => {
    const urlError = searchParams.get("error");
    if (urlError === "OAuthCallback") {
      setError(t('components.clientLogin.errorOAuthCallback'));
    } else if (urlError) {
      setError(t('components.clientLogin.errorGeneric'));
    }
  }, [searchParams, t]);

  // If already authenticated, redirect to dashboard
  useEffect(() => {
    if (status === "authenticated" && session) {
      router.replace("/client/dashboard");
    }
  }, [status, session, router]);

  const isInIframe = typeof window !== "undefined" && window.self !== window.top;

  const handleGoogleLogin = async () => {
    setError(null);
    setIsGoogleLoading(true);
    try {
      document.cookie = "auth_user_type=client; path=/; max-age=300";

      if (isInIframe) {
        // Google blocks OAuth inside iframes. Open the login page in a popup instead.
        // The popup loads this same page (outside iframe context), where the user
        // clicks Google sign-in normally. After auth completes, the popup auto-closes
        // and the iframe reloads to pick up the session.
        const width = 500;
        const height = 600;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        const popup = window.open(
          "/client/login?popup=true",
          "google-signin",
          `width=${width},height=${height},left=${left},top=${top},popup=yes`
        );

        if (!popup) {
          setError(t('components.clientLogin.errorPopupBlocked'));
          setIsGoogleLoading(false);
          return;
        }

        // Poll for popup close, then reload to pick up session
        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed);
            setIsGoogleLoading(false);
            // Reload to pick up the new session cookie
            window.location.reload();
          }
        }, 500);
      } else {
        await signIn("google", { callbackUrl: "/auth/callback" });
      }
    } catch (err: any) {
      setError(err.message || t('components.clientLogin.errorGoogleFailed'));
      setIsGoogleLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-[oklch(0.15_0.05_290)] to-[oklch(0.20_0.08_270)] text-foreground overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: "radial-gradient(circle, oklch(0.65 0.22 270 / 0.4) 1px, transparent 1px)",
            backgroundSize: "24px 24px",
          }}
        />
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16">
          <div className="inline-flex items-center gap-3 mb-8">
            <div className="p-3 bg-primary/15 backdrop-blur glow-sm rounded-xl">
              <FileText className="h-10 w-10" />
            </div>
          </div>
          <h1 className="text-4xl xl:text-5xl font-bold leading-tight mb-4">
            {t('components.clientLogin.heroHeading')}
          </h1>
          <p className="text-lg text-muted-foreground mb-10">
            {t('components.clientLogin.heroSubtitle')}
          </p>
          <div className="space-y-5">
            {[
              { icon: Upload, text: t('components.clientLogin.heroFeature1') },
              { icon: Eye, text: t('components.clientLogin.heroFeature2') },
              { icon: Clock, text: t('components.clientLogin.heroFeature3') },
            ].map((item) => (
              <div key={item.text} className="flex items-start gap-3">
                <item.icon className="h-5 w-5 mt-0.5 text-primary/80 shrink-0" />
                <span className="text-foreground/90">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-background relative">
        {/* Top-right language switcher */}
        <div className="absolute top-4 right-4">
          <LanguageSwitcher variant="text" />
        </div>
        <div className="w-full max-w-sm">
          {!isInIframe && (
            <div className="flex items-center mb-8">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/billance-ledger-logo.png"
                alt="Billance Ledger"
                className="h-14 w-auto"
              />
            </div>
          )}

          <h2 className="text-2xl font-bold mb-1">{t('components.clientLogin.title')}</h2>
          <p className="text-muted-foreground mb-8">{t('components.clientLogin.subtitle')}</p>

          {error && (
            <div className="p-3 mb-6 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg">
              {error}
            </div>
          )}

          {showEmailLogin ? (
            <EmailLoginForm onBack={() => setShowEmailLogin(false)} />
          ) : (
            <div className="space-y-4">
              <Button
                onClick={handleGoogleLogin}
                variant="outline"
                className="w-full h-11"
                disabled={isGoogleLoading}
              >
                {isGoogleLoading ? (
                  t('components.clientLogin.signingIn')
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    {t('common.signInWithGoogle')}
                  </>
                )}
              </Button>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">{t('components.clientLogin.or')}</span>
                </div>
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowEmailLogin(true)}
              >
                <Mail className="mr-2 h-4 w-4" /> {t('components.clientLogin.loginWithEmail')}
              </Button>
            </div>
          )}

          <div className="mt-8 pt-6 border-t text-center text-sm text-muted-foreground">
            <p>
              {t('components.clientLogin.bookkeeperQuestion')}{" "}
              <Link href="/login" className="text-primary hover:underline font-medium">
                {t('components.clientLogin.goToBookkeeperLogin')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ClientLoginPage() {
  return (
    <Suspense fallback={<ClientLoginFallback />}>
      <ClientLoginContent />
    </Suspense>
  );
}

function ClientLoginFallback() {
  const t = useTranslations();
  return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-muted-foreground">{t('components.clientLogin.loadingShort')}</p>
    </div>
  );
}
